import { isInstance, notNullish } from "@de-xima/xima-common-js-lang";
import jQuery from "jquery";

import type { IComponentHandlerCallbacks, IInsertData } from "../../Header.ts";
import { findWidgetForId } from "../../Util.ts";
import { AbstractComponentHandler } from "../AbstractComponentHandler.ts";

const SELECTOR_MONACO_EDITOR_WRAPPER = ".ui-monaco-editor";
const EVENT_MONACO_EDITOR_INITIALIZED = "monacoEditor:initialized";
const EDIT_ID = "MonacoEditorHandler#insertText";

/**
 * Handler for the monaco editor JSF wrapper.
 *
 * JavaScript library can be found here:
 * https://microsoft.github.io/monaco-editor/
 *
 * JSF wrapper is available here:
 * https://github.com/blutorange/primefaces-monaco
 */
export class MonacoEditorHandler extends AbstractComponentHandler {
  private _editId: number;

  constructor() {
    super();
    this._editId = 0;
  }

  override accepts($element: JQuery): boolean {
    return this._getWrapper($element).length === 1;
  }

  override focus($element: JQuery): void {
    const widget = this._getWidget($element);
    if (isInstance(widget, PrimeFaces.widget.ExtMonacoCodeEditorInline)) {
      widget.getMonaco()?.focus();
    } else if (isInstance(widget, PrimeFaces.widget.ExtMonacoCodeEditorFramed)) {
      widget.invokeMonaco("focus").catch((err) => console.error("Could not focus monaco editor", err));
    }
  }

  override getInputElement($element: JQuery): JQuery {
    const input = this._getWidget($element);
    return input?.jq ?? jQuery();
  }

  override initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): boolean {
    if (this._isReady($element)) {
      this._initialize($element, callbacks);
    } else {
      this._getWrapper($element).one(EVENT_MONACO_EDITOR_INITIALIZED, this._initialize.bind(this, $element, callbacks));
    }
    return true;
  }

  override insertText($element: JQuery, data: IInsertData): void {
    const widget = this._getWidget($element);
    if (isInstance(widget, PrimeFaces.widget.ExtMonacoCodeEditorInline)) {
      const editor = widget.getMonaco();
      if (notNullish(editor)) {
        const line = editor.getPosition();
        if (notNullish(line)) {
          const range = new monaco.Range(line.lineNumber, line.column, line.lineNumber, line.column);
          editor.executeEdits(this._getEditId(), [
            {
              forceMoveMarkers: true,
              range: range,
              text: data.content,
            },
          ]);
        }
      }
    } else if (isInstance(widget, PrimeFaces.widget.ExtMonacoCodeEditorFramed)) {
      // Script is serialized and transmitted via postMessage
      // DO NOT USE CLOSED OVER VARIABLES in the script function
      widget
        .invokeMonacoScript(
          (editor, editId, text) => {
            const line = editor.getPosition();
            if (notNullish(line)) {
              const range = new monaco.Range(line.lineNumber, line.column, line.lineNumber, line.column);
              editor.executeEdits(editId, [
                {
                  forceMoveMarkers: true,
                  range: range,
                  text: text,
                },
              ]);
            }
          },
          this._getEditId(),
          data.content,
        )
        .catch((e) => console.error("Could not insert text into monaco", e));
    }
  }

  /**
   * @returns The edit identifier for inserting text.
   */
  private _getEditId(): string {
    this._editId += 1;
    return `${EDIT_ID}:${this._editId}`;
  }

  /**
   * @param $element Input component.
   * @returns Monaco editor widget instance.
   */
  private _getWidget(
    $element: JQuery,
  ): PrimeFaces.widget.ExtMonacoCodeEditorInline | PrimeFaces.widget.ExtMonacoCodeEditorFramed | undefined {
    const $wrapper = this._getWrapper($element);
    const id = $wrapper.attr("id") ?? "";
    return (
      findWidgetForId(id, PrimeFaces.widget.ExtMonacoCodeEditorInline) ??
      findWidgetForId(id, PrimeFaces.widget.ExtMonacoCodeEditorFramed)
    );
  }

  /**
   * @param $element Input component.
   * @returns The wrapping div of the editor.
   */
  private _getWrapper($element: JQuery): JQuery {
    if ($element.is(SELECTOR_MONACO_EDITOR_WRAPPER)) {
      return $element;
    }
    return $element.find(SELECTOR_MONACO_EDITOR_WRAPPER);
  }

  /**
   * Same as the initialize method, but called once the monaco
   * editor is ready and initialized.
   * @param $element Input component.
   * @param callbacks Callbacks we can invoke.
   */
  private _initialize($element: JQuery, callbacks: IComponentHandlerCallbacks): void {
    const widget = this._getWidget($element);
    if (isInstance(widget, PrimeFaces.widget.ExtMonacoCodeEditorInline)) {
      const editor = widget.getMonaco();
      if (editor !== undefined) {
        editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyB, () => callbacks.onPlaceholderRequested(), "");
        editor.onDidFocusEditorWidget(() => callbacks.onFocus());
      }
    } else if (isInstance(widget, PrimeFaces.widget.ExtMonacoCodeEditorFramed)) {
      console.warn("Framed editor not completely supported yet for placeholder handler.");
    }
  }

  /**
   * @param $element Input component.
   * @returns Whether the monaco editor was already initialized.
   */
  private _isReady($element: JQuery): boolean {
    return this._getWidget($element)?.isReady() ?? false;
  }
}
